import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { CategoryManagementModalProps } from '../../../types/services.types';
import { useGetAllCategories } from '../../../api/category';
import { useAttachCategories, useDetachCategories } from '../../../api/service';
import { categoryManagementModalStyles } from './CategoryManagementModal.styles';

const CategoryManagementModal: React.FC<CategoryManagementModalProps> = ({ 
  isOpen, 
  onClose, 
  service 
}) => {
  // Changed from string[] to number[] to match API expectations
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  
  const { categories = [] } = useGetAllCategories();
  
  // Fix: Properly handle the service ID type conversion
  const serviceId = service?.id ? (typeof service.id === 'string' ? parseInt(service.id, 10) : service.id) : 0;
  const attachCategoriesMutation = useAttachCategories(serviceId);
  const detachCategoriesMutation = useDetachCategories(serviceId);

  useEffect(() => {
    if (service?.sign_categories) {
      // Extract number IDs from category objects
      setSelectedCategories(service.sign_categories.map(c => c.id));
    }
  }, [service]);

  // Changed parameter type from string to number
  const handleToggleCategory = async (categoryId: number): Promise<void> => {
    const isCurrentlySelected = selectedCategories.includes(categoryId);
    
    try {
      if (isCurrentlySelected) {
        // Detach category
        await detachCategoriesMutation.mutateAsync([categoryId]);
        setSelectedCategories(prev => prev.filter(id => id !== categoryId));
      } else {
        // Attach category
        await attachCategoriesMutation.mutateAsync([categoryId]);
        setSelectedCategories(prev => [...prev, categoryId]);
      }
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  if (!isOpen || !service?.id) return null;

  const isLoading = attachCategoriesMutation.isPending || detachCategoriesMutation.isPending;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={categoryManagementModalStyles.dialog}
    >
      <DialogTitle sx={categoryManagementModalStyles.title}>
        <Box sx={categoryManagementModalStyles.titleContainer}>
          <Typography variant="h6" component="h2">
            Manage Categories for {service.name}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={categoryManagementModalStyles.closeButton}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={categoryManagementModalStyles.content}>
        <Box sx={categoryManagementModalStyles.categoriesList}>
          {categories.map(category => {
            const isSelected = selectedCategories.includes(category.id);
            return (
              <Paper
                key={category.id}
                sx={categoryManagementModalStyles.categoryItem}
                elevation={1}
              >
                <Typography variant="body2" sx={categoryManagementModalStyles.categoryName}>
                  {category.name}
                </Typography>
                
                <Button
                  onClick={() => handleToggleCategory(category.id)}
                  variant={isSelected ? "outlined" : "contained"}
                  size="small"
                  disabled={isLoading}
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={16} />
                    ) : isSelected ? (
                      <LinkOffIcon />
                    ) : (
                      <LinkIcon />
                    )
                  }
                  sx={
                    isSelected 
                      ? categoryManagementModalStyles.detachButton 
                      : categoryManagementModalStyles.attachButton
                  }
                >
                  {isSelected ? 'Detach' : 'Attach'}
                </Button>
              </Paper>
            );
          })}
        </Box>
      </DialogContent>
      
      <DialogActions sx={categoryManagementModalStyles.actions}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={categoryManagementModalStyles.closeActionButton}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryManagementModal;